import React, { useEffect, useRef, useState } from 'react';
import { Loader2, Minus, Plus } from 'lucide-react';
import { ITEM_QTY_MAX, ITEM_QTY_MIN } from '../shared/orderConstants';

/*
 * Quantity control for a line.
 *
 * The draft is local and only committed on blur or Enter, because every commit
 * is a network call that moves stock. Typing "12" over a "1" would otherwise
 * fire a request for "1" on the way past — reserving the wrong stock and
 * possibly failing on a shortfall that doesn't exist.
 *
 * The steppers commit immediately, since a click is already a deliberate,
 * single-value change.
 */
const QuantityStepper = ({ value, onCommit, disabled, isPending }) => {
  const [draft, setDraft] = useState(String(value ?? ''));
  const inputRef = useRef(null);

  // Adopt server-confirmed values (including a rollback after a refusal).
  useEffect(() => {
    setDraft(String(value ?? ''));
  }, [value]);

  const commit = (next) => {
    const parsed = Number(next);
    if (
      !Number.isFinite(parsed) ||
      parsed < ITEM_QTY_MIN ||
      parsed > ITEM_QTY_MAX ||
      parsed === Number(value)
    ) {
      setDraft(String(value ?? ''));
      return;
    }
    onCommit(parsed);
  };

  const step = (delta) => {
    const next = Number(value) + delta;
    if (next < ITEM_QTY_MIN || next > ITEM_QTY_MAX) return;
    onCommit(next);
  };

  if (disabled) {
    return (
      <span className="text-[13px] font-medium text-[#3B4A54] tabular-nums">
        {value}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-0.5">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={isPending || Number(value) <= ITEM_QTY_MIN}
        aria-label="Decrease quantity"
        className="w-6 h-6 inline-flex items-center justify-center rounded-md border border-[#D6DFE2] bg-white text-[#4A5B66] cursor-pointer hover:bg-[#F5F8F8] disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
      >
        <Minus className="w-3 h-3" aria-hidden="true" />
      </button>

      <span className="relative inline-flex items-center">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          value={draft}
          disabled={isPending}
          aria-label="Quantity"
          onChange={(event) =>
            setDraft(event.target.value.replace(/[^\d]/g, ''))
          }
          onBlur={() => commit(draft)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              inputRef.current?.blur();
            }
            if (event.key === 'Escape') {
              setDraft(String(value ?? ''));
              inputRef.current?.blur();
            }
          }}
          className="w-11 h-6 text-center rounded-md border border-[#D6DFE2] bg-white text-[13px] font-semibold text-nysmah-dark tabular-nums focus:outline-none focus:border-nysmah-accent focus:ring-1 focus:ring-nysmah-accent disabled:opacity-50"
        />
        {isPending && (
          <Loader2
            className="absolute -right-4 w-3 h-3 animate-spin text-nysmah-muted"
            aria-hidden="true"
          />
        )}
      </span>

      <button
        type="button"
        onClick={() => step(1)}
        disabled={isPending || Number(value) >= ITEM_QTY_MAX}
        aria-label="Increase quantity"
        className="w-6 h-6 inline-flex items-center justify-center rounded-md border border-[#D6DFE2] bg-white text-[#4A5B66] cursor-pointer hover:bg-[#F5F8F8] disabled:opacity-35 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-nysmah-accent"
      >
        <Plus className="w-3 h-3" aria-hidden="true" />
      </button>
    </span>
  );
};

export default QuantityStepper;
